import React, { useEffect, useState, useRef, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import SideBar from "./RetailBusinessSideBar";
import { API_URL } from "../../constant";

const BusinessLayout = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [containerStyle, setContainerStyle] = useState({});
  const [inactivityTimeout, setInactivityTimeout] = useState(15*60000);
  
  // Refs to track timers
  const logoutTimerRef = useRef(null);
  const eventsSetupRef = useRef(false);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    fetchUserData();
    
    // Cleanup on unmount
    return () => {
      clearTimeout(logoutTimerRef.current);
      removeEventListeners();
    };
  }, []);

  // Fetch timeout configuration from Laravel backend
  const fetchTimeoutConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) return;

      // Fetch timeout configuration from Laravel API
      const configResponse = await fetch(`${API_URL}/api/config/inactivity-timeout`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      });

      if (configResponse.ok) {
        const configData = await configResponse.json();
        // Laravel response format: { "timeout_minutes": 1, "message": "Success" }
        const timeoutMinutes = configData.timeout_minutes || 1;
        setInactivityTimeout(timeoutMinutes * 60000); // Convert minutes to milliseconds
        
        // Also store in localStorage for quick access
        localStorage.setItem('inactivity_timeout', timeoutMinutes);
      }
    } catch (error) {
      console.error("Error fetching timeout config:", error);
      // Use localStorage fallback if API call fails
      const storedTimeout = localStorage.getItem('inactivity_timeout');
      if (storedTimeout) {
        setInactivityTimeout(parseInt(storedTimeout) * 60000);
      }
    }
  }, []);

  // Function to reset the logout timer
  const resetLogoutTimer = useCallback(() => {
    clearTimeout(logoutTimerRef.current);
    lastActivityRef.current = Date.now();
    
    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, inactivityTimeout);
  }, [inactivityTimeout]);

  // Function to handle user logout
  const handleLogout = useCallback(() => {
    clearTimeout(logoutTimerRef.current);
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_type");
    localStorage.removeItem("inactivity_timeout");
    navigate("/retail-agent-login");
  }, [navigate]);

  // Function to track user activity
  const trackActivity = useCallback(() => {
    // Only reset timer if user is active for more than 1 second since last activity
    const now = Date.now();
    if (now - lastActivityRef.current > 1000) {
      resetLogoutTimer();
    }
  }, [resetLogoutTimer]);

  // Function to setup event listeners
  const setupEventListeners = useCallback(() => {
    if (eventsSetupRef.current) return;
    
    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, trackActivity);
    });
    
    // Also listen for visibility change (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    eventsSetupRef.current = true;
  }, [trackActivity]);

  // Handle visibility change (tab switching)
  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden) {
      // User switched back to tab, reset timer
      resetLogoutTimer();
    }
  }, [resetLogoutTimer]);

  // Function to remove event listeners
  const removeEventListeners = useCallback(() => {
    if (!eventsSetupRef.current) return;
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.removeEventListener(event, trackActivity);
    });
    
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    
    eventsSetupRef.current = false;
  }, [trackActivity, handleVisibilityChange]);

  const fetchUserData = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/retail-agent-login");
      return;
    }

    setIsLoading(true);
    try {
      // Get user data only once
      const userResponse = await fetch(`${API_URL}/api/retail-business/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      });

      if (!userResponse.ok) {
        // If token is invalid, clear localStorage and redirect
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_type");
        localStorage.removeItem("inactivity_timeout");
        throw new Error("Invalid or expired token");
      }
      
      const responseData = await userResponse.json();

      const transformedUser = {
        firstName: responseData.user.first_name,
        sureName: responseData.user.last_name,
        fullName: `${responseData.user.first_name} ${responseData.user.last_name}`,
        role:
          responseData.user.role === "SeniorBusinessAdsAgent"
            ? "Senior Business Ads Agent"
            : "Mini Business Ads Agent",
        avatar: responseData.user.photo_url || "",
        email: responseData.user.email,
        id: responseData.user.retail_business_id || responseData.user.id,
        retail_business_id: responseData.user.retail_business_id || responseData.user.id,
        user_id: responseData.user.user_id,
        referralCode: responseData.user.referral_code,
        retailType: responseData.user.shop_details.retail_type,
        retailName: responseData.user.shop_details.retail_name,
      };

      setUserData(transformedUser);
      
      // Set background based on role (retail business gets #80302b to white)
      setContainerStyle(getContainerStyle());
      
      // Fetch timeout configuration and setup activity tracking
      await fetchTimeoutConfig();
      
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError(error.message);
      // Redirect to login on error
      navigate("/retail-agent-login");
    } finally {
      setIsLoading(false);
    }
  };

  // Setup activity tracking when userData is available
  useEffect(() => {
    if (userData && !eventsSetupRef.current) {
      resetLogoutTimer();
      setupEventListeners();
    }
    
    return () => {
      clearTimeout(logoutTimerRef.current);
    };
  }, [userData, resetLogoutTimer, setupEventListeners]);

  // Reset timer when inactivityTimeout changes
  useEffect(() => {
    if (userData) {
      resetLogoutTimer();
    }
  }, [inactivityTimeout, userData, resetLogoutTimer]);

  const getContainerStyle = () => {
    return {
      display: "flex",
      flexDirection: "column",
      marginTop: "0px",
      marginBottom: "0px",
      background: "linear-gradient(to bottom, #80302b, white)",
      width: "100%",
      minHeight: "100vh",
      fontFamily: "'Poppins', sans-serif"
    };
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ marginTop: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ color: "red" }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <SideBar user={userData} />
      <Outlet context={{ userData, fetchUserData, handleLogout }} />
    </div>
  );
};

export default BusinessLayout;